// src/features/developer/file-to-base64/FileToBase64Converter.tsx
import React, { useState, useCallback } from 'react';
import {
    Box, Paper, Typography, Stack, FormControlLabel, Checkbox, TextField,
    InputAdornment, IconButton, Tooltip
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import { Accept } from 'react-dropzone';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ClearIcon from '@mui/icons-material/Clear';
import CheckIcon from '@mui/icons-material/Check';

// Reusable components from other features (as per instruction).
// In a real-world scenario, these would be moved to a common directory.
import FileDropzone from '../../pdf/components/FileDropzone';
import DraggableFileList from '../../pdf/components/DraggableFileList';
import StyledFAQ from '../../common/components/StyledFAQ';

import { fileToBase64 } from '../utils/base64Utils';
import ToolActionArea from '../../pdf/components/ToolActionArea';
import { ToolStatus } from '../../pdf/components/ToolActionArea';

interface FileWithId {
    id: string;
    file: File;
}

const FileToBase64Converter: React.FC = () => {
    const { t } = useTranslation();
    const [file, setFile] = useState<FileWithId | null>(null);
    const [status, setStatus] = useState<ToolStatus>('idle');
    const [error, setError] = useState<string>('');
    const [base64Result, setBase64Result] = useState<string>('');
    const [includePrefix, setIncludePrefix] = useState<boolean>(true);
    const [isCopied, setIsCopied] = useState<boolean>(false);

    const resetState = useCallback(() => {
        setFile(null);
        setStatus('idle');
        setError('');
        setBase64Result('');
    }, []);

    const handleDrop = useCallback((acceptedFiles: File[]) => {
        resetState();
        if (acceptedFiles.length > 0) {
            setFile({ id: uuidv4(), file: acceptedFiles[0] });
        }
    }, [resetState]);

    const handleDelete = useCallback(() => {
        resetState();
    }, [resetState]);

    const handleConvert = async () => {
        if (!file) {
            setError(t('file_to_base64_error_no_file', { ns: 'tools' }));
            setStatus('error');
            return;
        }
        setStatus('loading');
        setError('');
        try {
            const result = await fileToBase64(file.file, includePrefix);
            setBase64Result(result);
            setStatus('success');
        } catch (e: any) {
            console.error(e);
            setError(t('file_to_base64_error_generic', { ns: 'tools' }));
            setStatus('error');
        }
    };

    const handleCopy = () => {
        navigator.clipboard.writeText(base64Result);
        setIsCopied(true);
        setTimeout(() => setIsCopied(false), 2000);
    };

    // We only accept any file type
    const acceptedFiles: Accept = {};

    const faqs = [
        { q: 'file_to_base64_faq1_q', a: 'file_to_base64_faq1_a' },
        { q: 'file_to_base64_faq2_q', a: 'file_to_base64_faq2_a' },
    ];

    return (
        <>
            <Paper elevation={0} sx={{ p: { xs: 2, md: 4 }, bgcolor: 'background.paper', borderRadius: 2 }}>
                <Stack spacing={4} alignItems="center">
                    <FileDropzone
                        onDrop={handleDrop}
                        accept={acceptedFiles}
                        multiple={false}
                        disabled={status === 'loading'}
                    />

                    {file && (
                        <DraggableFileList
                            files={[file]}
                            onReorder={() => {}} // No reordering for a single file
                            onDelete={handleDelete}
                            isDraggable={false} // Only one file, no need to drag
                        />
                    )}

                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={includePrefix}
                                onChange={(e) => setIncludePrefix(e.target.checked)}
                                disabled={status === 'loading'}
                            />
                        }
                        label={t('file_to_base64_include_prefix', { ns: 'tools' })}
                    />

                    <ToolActionArea
                        status={status}
                        onAction={handleConvert}
                        onReset={resetState}
                        actionButtonText={t('file_to_base64_action_button', { ns: 'tools' })}
                        successTitle={t('file_to_base64_success_title', { ns: 'tools' })}
                        successMessage={t('file_to_base64_success_desc', { ns: 'tools' })}
                        errorMessage={error}
                        disabled={!file || status === 'loading'}
                    >
                        {/* Custom content for success state */}
                        {status === 'success' && base64Result && (
                            <TextField
                                fullWidth
                                multiline
                                rows={8}
                                variant="outlined"
                                label={t('file_to_base64_output_label', { ns: 'tools' })}
                                value={base64Result}
                                InputProps={{
                                    readOnly: true,
                                    sx: {
                                        fontFamily: 'monospace',
                                        fontSize: '0.875rem',
                                    },
                                    endAdornment: (
                                        <InputAdornment position="end" sx={{ alignSelf: 'flex-start', mt: 1 }}>
                                            <Stack>
                                                <Tooltip title={isCopied ? t('file_to_base64_copied', { ns: 'tools' }) : t('file_to_base64_copy', { ns: 'tools' })}>
                                                    <IconButton onClick={handleCopy}>
                                                        {isCopied ? <CheckIcon color="success" /> : <ContentCopyIcon />}
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title={t('file_to_base64_clear', { ns: 'tools' })}>
                                                    <IconButton onClick={() => setBase64Result('')}>
                                                        <ClearIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            </Stack>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        )}
                    </ToolActionArea>
                </Stack>
            </Paper>

            <Box sx={{ my: 15 }}>
                <StyledFAQ
                    title={t('what_is_base64_title', { ns: 'tools' })}
                    description={t('what_is_base64_desc', { ns: 'tools' })}
                    faqs={[]}
                    t={t}
                />
                <StyledFAQ
                    title={t('how_to_use_this_tool_title', { ns: 'tools' })}
                    description={t('how_to_use_this_tool_file_to_base64_desc', { ns: 'tools' })}
                    faqs={[]}
                    t={t}
                />
                <StyledFAQ
                    title={t('why_use_our_tool_title', { ns: 'tools' })}
                    description={t('why_use_our_tool_desc', { ns: 'tools' })}
                    faqs={faqs}
                    t={t}
                />
            </Box>
        </>
    );
};

export default FileToBase64Converter;